import type { DomainName } from '../../../../types/models';
import type { DomainTradeRejectionCode } from '../../../../types/models/DomainOrder';

// Trade-wizard-specific state helpers. The registrant-contact helpers (`ContactDraft`,
// `EMPTY_CONTACT`, `isContactDraftValid`, `buildContactRequest`, `vatRateLabel`) and the
// `ContactMode` type are REUSED verbatim from the registration wizard (`../Register/types`) —
// import them from there. This file holds only what differs for an internal trade (a `.be`
// registrant/owner change): its own step ids, the client-side eligibility pre-filter, and the
// per-name rejection-code copy.

/**
 * Stable WizardStep ids for the trade wizard — its own set, shared between the `<Wizard>` step
 * definitions and the create-error step-jump logic (mirrors `TRANSFER_STEP_IDS`).
 */
export const TRADE_STEP_IDS = {
  domains: 'trade-domains',
  contact: 'trade-contact',
  recap: 'trade-recap',
} as const;

/** The most domains whose owner may be changed in a single order (backend cap). */
export const MAX_TRADE_CART = 25;

/** True when the FQDN is under the `.be` TLD — the only tradeable extension in v1. */
export function isBeDomain(name: string): boolean {
  return name.toLowerCase().endsWith('.be');
}

/**
 * Client-side pre-filter mirroring the backend's trade eligibility policy: returns `null` when the
 * domain looks tradeable, else a human reason to disable its row/action. Eligible = `.be` AND
 * registrar-bound (`domain_registration_platform` — the same signal as the Domains table's
 * Registrar column; NOT `is_managed`, which means DNS-managed via a PowerDNS endpoint) AND not in
 * a transfer window. The backend gate is authoritative — a name that passes here can still be
 * rejected at submit with a per-name code (surfaced via `tradeRejectionCopy`).
 */
export function tradeIneligibilityReason(
  domain: Pick<DomainName, 'name' | 'domain_registration_platform' | 'in_transfer'>,
): string | null {
  if (!isBeDomain(domain.name)) {
    return 'Only .be domains can change owner here.';
  }
  if (domain.domain_registration_platform === null) {
    return 'This domain is not registrar-managed by Ancalagon.';
  }
  if (domain.in_transfer) {
    return 'This domain is currently in a transfer.';
  }
  return null;
}

// Human copy for the per-name machine codes a trade create rejects with (`data.domains` map).
// Any rejected name fails the WHOLE order, so every code needs actionable wording.
const TRADE_REJECTION_COPY: Record<DomainTradeRejectionCode, string> = {
  tld_not_tradeable: 'Only .be domains can change owner here.',
  domain_not_found: 'This domain does not belong to your organization.',
  domain_not_managed_here: 'This domain is not registrar-managed by Ancalagon.',
  domain_in_transfer: 'This domain is currently in a transfer.',
  order_in_progress: 'Another order already references this domain.',
  tld_not_offered: "We don't offer owner changes for this domain's extension.",
};

/**
 * Friendly copy for a create-time per-name rejection code. Falls back to the raw value for anything
 * unmapped, mirroring the transfer wizard's `conflictCopy`.
 */
export function tradeRejectionCopy(code: string): string {
  return (TRADE_REJECTION_COPY as Record<string, string>)[code] ?? code;
}
